import { useState } from "react"

import type { SignedUrl } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseInvoiceDocumentDeleteReturn {
  isConfirmationOpen: boolean
  isDeleting: boolean
  handleDeleteRequest: (document: SignedUrl) => void
  handleDeleteCancel: () => void
  handleDeleteConfirm: () => Promise<void>
}

export const useInvoiceDocumentDelete = (refetch: (immediate?: boolean) => void): UseInvoiceDocumentDeleteReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [pendingFileId, setPendingFileId] = useState<string | null>(null)
  // `immediate: true` — a deletion has no upload-processing lag, so the list should
  // reflect it by the time the success notification fires (unlike the default post-upload refetch).
  const { removeSingleFile, removingInProgress } = useFilesRemove(() => refetch(true))

  const handleDeleteRequest = (document: SignedUrl) => {
    setPendingFileId(document.file?.fileId ?? null)
  }

  const handleDeleteCancel = () => setPendingFileId(null)

  const handleDeleteConfirm = async () => {
    if (!pendingFileId) return

    const isSuccess = await removeSingleFile(pendingFileId)
    if (isSuccess) {
      notify({ type: NotificationType.Success, message: "Document removed" })
    } else {
      notify({ type: NotificationType.Error, message: "Failed to remove document. Please try again." })
    }
    setPendingFileId(null)
  }

  return {
    isConfirmationOpen: pendingFileId !== null,
    isDeleting: removingInProgress,
    handleDeleteRequest,
    handleDeleteCancel,
    handleDeleteConfirm,
  }
}
